import { GetPricingExportData } from "api/revue/underwriteData";
import { pricingExportFieldSettings } from "components/underwrite/acquisition/export/pricingExportFieldSettings";
import ExcelJS from 'exceljs';

const MAX_COLUMN_WIDTH = 50;

export const pricingExport = async (opportunityIds) => {
    const rawData = sortAccordingToFieldSettings(await (await GetPricingExportData(opportunityIds)).json());
    const formattedData = formatFields(rawData);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1', {
        views: [{ state: "frozen", ySplit: 1 }],
    });

    worksheet.columns = pricingExportFieldSettings
        .map(({ fieldName, header, type, decimalPlaces }) => ({ header: header, key: fieldName, style: { numFmt: getColumnFormat(type, decimalPlaces) }, width: header.length }))
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
        cell.font = {
            bold: true,
        };
    });
    worksheet.addRows(formattedData);
    paintHeaderRow(headerRow);
    setColumnWidthBasedOnContent(worksheet.columns, worksheet, formattedData);
    generateExcel(workbook);
}

const setColumnWidthBasedOnContent = (columns, worksheet, data) => {
    columns.forEach((header, colIndex) => {
        let headerWidth = header.width;
        let contentMaxWidth = headerWidth;
        const colSetting = pricingExportFieldSettings.find(s => s.fieldName === header._key);
        const userDefinedWidth = colSetting.width;
        data.forEach(row => {
            let cellValue = row[header._key] ? row[header._key].toString() : '';
            if(colSetting.decimalPlaces > 0) {
                cellValue = cellValue ? Number(cellValue).toFixed(colSetting.decimalPlaces) : ''
            }
            contentMaxWidth = Math.max(contentMaxWidth, cellValue.length);
        });
        let width = userDefinedWidth ?? contentMaxWidth;
        worksheet.getColumn(colIndex + 1).width = (MAX_COLUMN_WIDTH < width ? MAX_COLUMN_WIDTH : width) + 4; // Add some padding
    });
}

const getColumnFormat = (type, decimalPlaces) => {
    if (type !== 'number' && type !== 'currency') return undefined;
    if (!decimalPlaces) return `${type === 'currency' ? '"$"' : ''}#,##0`;
    return `${type === 'currency' ? '"$"' : ''}#,##0.${'0'.repeat(decimalPlaces)}`;
}

const paintHeaderRow = (headerRow) => {
    const propertyDataStart = 1;
    const underwriteDataStart = 11;
    const compDataStart = 20;

    for (let i = propertyDataStart; i < underwriteDataStart; i++) {
        headerRow._cells[i - 1].style.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'EDEDED' }, // #EDEDED background color
        };
    }

    for (let i = underwriteDataStart; i < compDataStart; i++) {
        headerRow._cells[i - 1].style.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF2CC' }, // #FFF2CC background color
        };
    }

    for (let i = compDataStart; i <= headerRow._cells.length; i++) {
        headerRow._cells[i - 1].style.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'D9E1F2' }, // #D9E1F2 background color
        };
    }
}

const formatFields = (rawData) => {
    const data = rawData.map(v => {
        for (const [key, value] of Object.entries(v)) {
            const fieldSetting = pricingExportFieldSettings.find(s => s.fieldName === key);
            if (fieldSetting.type === 'DateAndTime') v[key] = value ? new Date(value).toLocaleString('en') : ''
            if (fieldSetting.type === 'Date') v[key] = value ? new Date(value).toLocaleDateString('en') : ''
        }
        return v;
    })
    return data;
}

const generateExcel = (workbook) => {
    // Generate the Excel file as a blob
    workbook.xlsx.writeBuffer().then(buffer => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);

        // Create a download link and trigger a click event
        const a = document.createElement('a');
        a.href = url;
        a.download = `Pricing Export ${new Date().toLocaleString('en')}.xlsx`;
        a.click();

        // Clean up
        window.URL.revokeObjectURL(url);
    });
}

const sortAccordingToFieldSettings = (data) => {
    const mappedSettings = pricingExportFieldSettings.map((v, index) => {
        v.order = index;
        return v;
    })
    const orderedData = data.map(v => {
        let row = {}
        const entries = Object.entries(v);
        for (const { fieldName } of mappedSettings) {
            const responseAndSettingsMatch = entries.find((v) => fieldName === v[0]);
            if (responseAndSettingsMatch) {
                row[fieldName] = entries.find((v) => fieldName === v[0])[1]
            }
        }
        return row;
    })
    return orderedData;
}